import React, { useCallback, useMemo } from 'react';
import PlayIconFilled from '@moda/icons/play-filled-72';
import PauseIconFilled from '@moda/icons/pause-filled-72';
import { gql } from '@apollo/client';
import SoundOffIcon from '@moda/icons/sound-off-36';
import SoundOnIcon from '@moda/icons/sound-on-36';
import { Clickable, useBreakpoint } from '@moda/om';
import classNames from 'classnames';
import { ProductVideosFragment } from '../../generated/types';
import { useVideoPlayer, VideoPlayer } from '../VideoPlayer';
import { VideoProgressBar } from '../VideoProgressBar';
import { useVideoIconSizeProps } from '../../hooks/useVideoIconSizeProps';
import { Loading } from '../Loading';

import './ProductDetailPageVideo.scss';

const MOBILE_SIZE_PROP = { lt: 'md' };

type Video = NonNullable<ProductVideosFragment['videos']>[number];

export const PRODUCT_VIDEOS_FRAGMENT = gql`
  fragment ProductVideosFragment on Variant {
    videos {
      id
      width
      height
      hasAudio: has_audio
    }
  }
`;

interface Props {
  videoId: string | null;
  videos: Video[] | null;
}

export const ProductDetailPageVideo: React.FC<Props> = ({ videoId, videos }) => {
  const currentVideo = useMemo(
    () => videos?.find(_video => _video.id === videoId),
    [videoId, videos]
  );
  const { matches: isMobile } = useBreakpoint(MOBILE_SIZE_PROP);

  const {
    initialize: onLoad,
    player,
    isPlaying,

    loading,
    muted,
    handleMute,
    isHovered,
    setHovered,
    showControls
  } = useVideoPlayer({ hasAudio: currentVideo?.hasAudio ?? false, isMobile });

  const iconSizeProps = useVideoIconSizeProps(MOBILE_SIZE_PROP);

  const handleClick = useCallback(() => {
    if (!player) return;

    if (isPlaying) {
      player.pause();
    }

    if (!isPlaying) {
      player.play();
    }
  }, [isPlaying, player]);

  const handleOver = useCallback(() => {
    if (isMobile) return;

    setHovered(true);
  }, [isMobile, setHovered]);

  const handleLeave = useCallback(() => {
    if (isMobile) return;

    setHovered(false);
  }, [isMobile, setHovered]);

  if (!currentVideo) return null;

  return (
    <div
      className="ProductDetailPageVideo"
      onFocus={handleOver}
      onMouseOver={handleOver}
      onMouseLeave={handleLeave}
    >
      {loading && (
        <div className="ProductDetailPageVideo__loading">
          <Loading />
        </div>
      )}

      {showControls && (
        <>
          <Clickable
            aria-label={isPlaying ? 'pause' : 'play'}
            className={classNames(
              'ProductDetailPageVideo__button',
              'ProductDetailPageVideo__play-pause-button',
              { 'ProductDetailPageVideo__play-button--no-sound': !currentVideo.hasAudio }
            )}
            onClick={handleClick}
          >
            <PauseIconFilled
              className={!isPlaying ? 'ProductDetailPageVideo__item--hidden' : undefined}
              {...iconSizeProps}
            />

            <PlayIconFilled
              className={isPlaying ? 'ProductDetailPageVideo__item--hidden' : undefined}
              {...iconSizeProps}
            />
          </Clickable>

          {currentVideo.hasAudio && (
            <div
              aria-label={muted ? 'unmute' : 'mute'}
              className="ProductDetailPageVideo__button ProductDetailPageVideo__sound-button"
              onClick={handleMute}
              onKeyDown={handleMute}
              role="button"
              tabIndex={0}
            >
              <SoundOffIcon
                className={!muted ? 'ProductDetailPageVideo__item--hidden' : undefined}
                {...iconSizeProps}
              />

              <SoundOnIcon
                className={muted ? 'ProductDetailPageVideo__item--hidden' : undefined}
                {...iconSizeProps}
              />
            </div>
          )}
        </>
      )}

      <VideoPlayer
        key={videoId}
        loop
        playerProps={{
          embedOptions: {
            responsive: {
              aspectRatio: `${currentVideo.width}:${currentVideo.height}`
            }
          },
          videoId: currentVideo.id
        }}
        onLoad={onLoad}
      />

      <VideoProgressBar player={player} show={isHovered || isMobile} />
    </div>
  );
};
